/**
 * Configuración por rango temporal del Gantt de Planificación Avanzada.
 *
 * Recoge en un solo sitio lo que antes vivía como constantes y literales
 * repartidos por cada página duplicada: títulos, capacidad de referencia, claves
 * de los KPIs y nombres de la exportación a Excel.  Añadir un rango nuevo es
 * añadir una entrada aquí más su builder en `planningTimeTracks.ts`.
 */
import type { PlanVista } from '@/components/planning/ViewModeSelect'

export interface VistaConfig {
  /** Clave i18n del título de la página. */
  titleKey: string
  /** Clave i18n del subtítulo. */
  subtitleKey: string
  /** Capacidad productiva de referencia del rango, en unidades. */
  capacidadMax: number
  /** Clave i18n del KPI de demanda comprometida. */
  demandKpiKey: string
  /** Clave i18n del KPI de capacidad disponible. */
  capacityKpiKey: string
  /** Clave i18n del nombre de la hoja Excel. */
  excelSheetKey: string
  /** Clave i18n del nombre del fichero Excel. */
  excelFilenameKey: string
  /** Modo que espera `DateNavPicker` para su selector emergente. */
  navMode: 'week' | 'month' | 'year'
}

/** Capacidad semanal de referencia. Las demás escalan a partir de ella. */
const CAPACIDAD_SEMANAL = 560_000

export const PLAN_VISTA_CONFIG: Record<PlanVista, VistaConfig> = {
  semanal: {
    titleKey: 'planning.title',
    subtitleKey: 'planning.subtitle',
    capacidadMax: CAPACIDAD_SEMANAL,
    demandKpiKey: 'planning.kpi.committedDemand',
    capacityKpiKey: 'planning.kpi.availableCapacity',
    excelSheetKey: 'planning.excel.sheetName',
    excelFilenameKey: 'planning.excel.filename',
    navMode: 'week',
  },
  mensual: {
    titleKey: 'planning.monthlyTitle',
    subtitleKey: 'planning.monthlySubtitle',
    // 4 × semanal: el valor que ya usaba la vista mensual, se mantiene igual.
    capacidadMax: CAPACIDAD_SEMANAL * 4,
    demandKpiKey: 'planning.kpi.monthlyDemand',
    capacityKpiKey: 'planning.kpi.monthlyCapacity',
    excelSheetKey: 'planning.excel.sheetNameMonthly',
    excelFilenameKey: 'planning.excel.filenameMonthly',
    navMode: 'month',
  },
  anual: {
    titleKey: 'planning.yearlyTitle',
    subtitleKey: 'planning.yearlySubtitle',
    // 52 × semanal. OJO: extrapolación de la escala existente, no un dato de
    // negocio confirmado; el KPI de capacidad anual depende de este número.
    capacidadMax: CAPACIDAD_SEMANAL * 52,
    demandKpiKey: 'planning.kpi.yearlyDemand',
    capacityKpiKey: 'planning.kpi.yearlyCapacity',
    excelSheetKey: 'planning.excel.sheetNameYearly',
    excelFilenameKey: 'planning.excel.filenameYearly',
    navMode: 'year',
  },
}
